import { z } from 'zod'
import { UtrAssetResponseSchema } from './universal-token-rating'

export const UtrShareCardVariantSchema = z.enum(['square', 'wide'])
export type UtrShareCardVariant = z.infer<typeof UtrShareCardVariantSchema>

export const UTR_SHARE_CARD_SIZES = {
  square: { width: 1200, height: 1200 },
  wide: { width: 1200, height: 630 },
} as const satisfies Record<UtrShareCardVariant, { width: number; height: number }>

export const UtrShareCardAxisSchema = z.object({
  score: z.number(),
  grade: z.string(),
})
export type UtrShareCardAxis = z.infer<typeof UtrShareCardAxisSchema>

export const UtrShareCardDataSchema = z.object({
  name: z.string(),
  ticker: z.string(),
  image: z.string().nullable(),
  rank: z.number().int().nullable(),
  grade: z.string(),
  overallScore: z.number(),
  disclosure: UtrShareCardAxisSchema,
  performance: UtrShareCardAxisSchema,
  verified: z.boolean(),
  generatedAt: z.string(),
})
export type UtrShareCardData = z.infer<typeof UtrShareCardDataSchema>

export const UtrShareCardSourceSchema = UtrAssetResponseSchema.pick({
  arcticId: true,
  projectName: true,
  symbol: true,
  image: true,
  rank: true,
  grade: true,
  overallScore: true,
  notRated: true,
  verified: true,
  disclosure: true,
  performance: true,
  generatedAt: true,
  configVersion: true,
})
export type UtrShareCardSource = z.infer<typeof UtrShareCardSourceSchema>

const UTR_SHARE_CARD_FALLBACK_GRADE = 'N/A'

export interface ToUtrShareCardDataOptions {
  verified: boolean
}

function toShareCardAxis(axis: { score: number; grade: string | null } | null | undefined): UtrShareCardAxis {
  return {
    score: axis?.score ?? 0,
    grade: axis?.grade ?? UTR_SHARE_CARD_FALLBACK_GRADE,
  }
}

export function toUtrShareCardData(
  asset: UtrShareCardSource,
  { verified }: ToUtrShareCardDataOptions,
): UtrShareCardData | null {
  if (asset.notRated || asset.overallScore == null || !asset.grade) {
    return null
  }

  return {
    name: asset.projectName,
    ticker: asset.symbol.toUpperCase(),
    image: asset.image ?? null,
    rank: asset.rank ?? null,
    grade: asset.grade,
    overallScore: asset.overallScore,
    disclosure: toShareCardAxis(asset.disclosure),
    performance: toShareCardAxis(asset.performance),
    verified,
    generatedAt: asset.generatedAt,
  }
}

export function utrShareCardVersion(asset: Pick<UtrShareCardSource, 'configVersion' | 'generatedAt'>): string {
  const generatedAt = Date.parse(asset.generatedAt)
  return `${asset.configVersion}-${Number.isNaN(generatedAt) ? 0 : generatedAt}`
}
